import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTestApiRequest } from './queries';
import { useAuthStore } from '../../store/authStore';
import { generateCurlCommand } from './api';
import type { TestRequest, TestResponse } from './types';
import { defaultTestRequest } from './schemas';
import { JWTTester } from './JWTTester';

// Status code pill component
function StatusCodePill({ status }: { status: number }) {
  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'bg-green-100 text-green-800';
    if (status >= 300 && status < 400) return 'bg-yellow-100 text-yellow-800';
    if (status >= 400 && status < 500) return 'bg-red-100 text-red-800';
    if (status >= 500) return 'bg-red-100 text-red-900';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
      {status}
    </span>
  );
}

// Key-value editor for headers
function KeyValueEditor({ 
  title, 
  data, 
  onChange, 
  placeholder 
}: { 
  title: string; 
  data: Record<string, string>; 
  onChange: (data: Record<string, string>) => void;
  placeholder?: { key: string; value: string };
}) {
  const [entries, setEntries] = useState(
    Object.entries(data).concat([['', '']])
  );

  useEffect(() => {
    const newData = entries
      .filter(([key, value]) => key.trim() !== '' || value.trim() !== '')
      .reduce((acc, [key, value]) => {
        if (key.trim()) acc[key] = value;
        return acc;
      }, {} as Record<string, string>);
    
    if (JSON.stringify(newData) !== JSON.stringify(data)) {
      onChange(newData);
    }
  }, [entries, data, onChange]);

  const updateEntry = (index: number, key: string, value: string) => {
    const newEntries = [...entries];
    newEntries[index] = [key, value];
    
    // Add empty row if last row is filled
    if (index === entries.length - 1 && key.trim() !== '') {
      newEntries.push(['', '']);
    }
    
    setEntries(newEntries);
  };

  const removeEntry = (index: number) => {
    if (entries.length > 1) {
      const newEntries = entries.filter((_, i) => i !== index);
      setEntries(newEntries);
    }
  };

  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
      <div className="space-y-2">
        {entries.map(([key, value], index) => (
          <div key={index} className="flex space-x-2">
            <input
              type="text"
              placeholder={placeholder?.key || 'Key'}
              value={key}
              onChange={(e) => updateEntry(index, e.target.value, value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
            <input
              type="text"
              placeholder={placeholder?.value || 'Value'}
              value={value}
              onChange={(e) => updateEntry(index, key, e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
            />
            {entries.length > 1 && (
              <button
                onClick={() => removeEntry(index)}
                className="px-2 py-2 text-red-600 hover:text-red-800 focus:outline-none"
                type="button"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TestConsole() {
  const { t } = useTranslation();
  const { token, selectedTenant } = useAuthStore();
  const [request, setRequest] = useState<TestRequest>({
    ...defaultTestRequest,
    headers: {
      ...defaultTestRequest.headers,
      'x-tenant-id': selectedTenant || 'dev',
    },
    jwt: token || undefined,
  });
  const [useSessionJwt, setUseSessionJwt] = useState(true);

  const testMutation = useTestApiRequest();

  // Update tenant in headers when it changes
  useEffect(() => {
    setRequest(prev => ({
      ...prev,
      headers: {
        ...prev.headers,
        'x-tenant-id': selectedTenant || 'dev',
      },
    }));
  }, [selectedTenant]);

  // Update JWT when session changes
  useEffect(() => {
    if (useSessionJwt) {
      setRequest(prev => ({
        ...prev,
        jwt: token || undefined,
      }));
    }
  }, [token, useSessionJwt]);

  const handleSendRequest = () => {
    const finalRequest = {
      ...request,
      jwt: useSessionJwt ? (token || undefined) : request.jwt,
    };
    testMutation.mutate(finalRequest);
  };

  const copyAsCurl = () => {
    const finalRequest = {
      ...request,
      jwt: useSessionJwt ? (token || undefined) : request.jwt,
    };
    const curl = generateCurlCommand(finalRequest);
    navigator.clipboard.writeText(curl);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">
          {t('apis.testConsole.title', 'API Test Console')}
        </h1>
        <p className="text-gray-600 mt-1">
          {t('apis.testConsole.subtitle', 'Test API endpoints with or without authentication')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Request Panel */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t('apis.testConsole.request', 'Request')}
          </h2>

          {/* Method and URL */}
          <div className="grid grid-cols-4 gap-3 mb-4">
            <select
              value={request.method}
              onChange={(e) => setRequest(prev => ({ ...prev, method: e.target.value as any }))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
              <option value="PATCH">PATCH</option>
              <option value="HEAD">HEAD</option>
              <option value="OPTIONS">OPTIONS</option>
            </select>
            <input
              type="url"
              placeholder="https://dev.devcockpit.ai/api/..."
              value={request.url}
              onChange={(e) => setRequest(prev => ({ ...prev, url: e.target.value }))}
              className="col-span-3 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          {/* JWT Helper */}
          <div className="mb-4 p-3 bg-gray-50 rounded">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={useSessionJwt}
                onChange={(e) => setUseSessionJwt(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                {t('apis.testConsole.useSessionJwt', 'Use current session JWT')}
              </span>
            </label>
            {!useSessionJwt && (
              <textarea
                placeholder="Paste JWT token here..."
                value={request.jwt || ''}
                onChange={(e) => setRequest(prev => ({ ...prev, jwt: e.target.value }))}
                className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                rows={3}
              />
            )}
          </div>

          {/* Headers */}
          <div className="mb-4">
            <KeyValueEditor
              title={t('apis.testConsole.headers', 'Headers')}
              data={request.headers}
              onChange={(headers) => setRequest(prev => ({ ...prev, headers }))}
              placeholder={{ key: 'Header-Name', value: 'value' }}
            />
          </div>

          {/* Body (for non-GET requests) */}
          {request.method !== 'GET' && request.method !== 'HEAD' && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {t('apis.testConsole.requestBody', 'Request Body')}
              </h4>
              <textarea
                placeholder='{"key": "value"}'
                value={typeof request.body === 'string' ? request.body : JSON.stringify(request.body || '', null, 2)}
                onChange={(e) => {
                  try {
                    const parsed = JSON.parse(e.target.value);
                    setRequest(prev => ({ ...prev, body: parsed }));
                  } catch {
                    setRequest(prev => ({ ...prev, body: e.target.value }));
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm font-mono"
                rows={6}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleSendRequest}
              disabled={testMutation.isPending || !request.url}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {testMutation.isPending 
                ? t('apis.testConsole.sending', 'Sending...') 
                : t('apis.testConsole.send', 'Send Request')
              }
            </button>
            <button
              onClick={copyAsCurl}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              {t('apis.testConsole.copyCurl', 'Copy cURL')}
            </button>
          </div>
        </div>

        {/* Response Panel */}
        <div className="space-y-4">
          {testMutation.data && (
            <div className="bg-white border rounded-lg">
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center space-x-4">
                  <StatusCodePill status={testMutation.data.status} />
                  <span className="text-sm text-gray-600">
                    {testMutation.data.timing.totalMs}ms
                  </span>
                </div>
              </div>
              <div className="p-4">
                <pre className="bg-gray-50 p-3 rounded text-sm overflow-auto max-h-96">
                  {JSON.stringify(testMutation.data.body, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {testMutation.error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-red-700">
                Error: {testMutation.error.message}
              </div>
            </div>
          )}

          {!testMutation.data && !testMutation.error && !testMutation.isPending && (
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="text-gray-400 text-4xl mb-3">🛣️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('apis.testConsole.noResponse', 'No response yet')}
              </h3>
              <p className="text-gray-600">
                {t('apis.testConsole.noResponseDescription', 'Send a request to see the response here')}
              </p>
            </div>
          )}

          {testMutation.isPending && (
            <div className="bg-white p-8 rounded-lg shadow text-center">
              <div className="animate-spin text-blue-600 text-2xl mb-3">⟳</div>
              <h3 className="text-lg font-medium text-gray-900">
                {t('apis.testConsole.sending', 'Sending request...')}
              </h3>
            </div>
          )}
        </div>

        {/* JWT Tester Panel */}
        <div>
          <JWTTester />
        </div>
      </div>
    </div>
  );
}